import {
  type ConditionalDisplayConfig,
  onBehalfOfText,
  representativeWithRole,
} from "@repo/shared";
import type { MemberRepresentative } from "@repo/shared/types";
import { useT } from "@/lib/i18n/use-t";
import { PublicFlowComponentRenderer } from "../public-flow-component-renderer";
import { evaluateCondition } from "../public-flow-utils";
import type { PublicPageRendererContext } from "../public-page-renderer";
import { T } from "./member-session-flow-view.i18n";

type MemberSessionFlowViewProps = {
  allPages: any[];
  ctx: PublicPageRendererContext;
  memberName: string;
  /** QL-660 — the member's own POA/guardian, who is the one signed in. */
  memberRepresentative?: MemberRepresentative | null;
  /**
   * QL-653 — the session is ONE of several representatives of the member
   * (`memberName` is then the representative's own name): the banner names the
   * representative, on behalf of the member.
   */
  isExpandedLine?: boolean;
  language: string;
  /** QL-654 — the session is a representative's (not a group member's): identity verification applies. */
  isRepresentative?: boolean;
};

const MEMBER_SESSION_COMPONENT_TYPES = new Set(["product_assessment", "quiz"]);

/**
 * QL-654 — a representative (one of 2+) also verifies their OWN identity from
 * their personal link; a group member of a group with no representative has
 * nobody to verify, so the component stays out of their view.
 */
const REPRESENTATIVE_SESSION_COMPONENT_TYPES = new Set([
  ...MEMBER_SESSION_COMPONENT_TYPES,
  "sumsub_verification",
]);

/**
 * QL-505 — a member link (`/p/m/<token>`) creates a `PublicClientSession` with
 * `memberId` set. The backend confines that session to reads of the flow's
 * product-assessment and quiz components (`member-session-guard.ts`) and
 * denies everything else — saves of any other component, complete, sign, PDF
 * generation. So instead of threading a `sessionMemberId` read through the
 * normal multi-goal/single-goal + goal/portfolio page machinery (which would
 * have to special-case every other component's write path against a 403 it
 * can never pass), this renders a deliberately stripped view: no header
 * chrome, no progress bar, no Submit, no goal/portfolio navigation — just the
 * PA/quiz components, in page order.
 *
 * Both component types are GLOBAL_ONLY_COMPONENTS in the placement policy
 * (`packages/shared/src/flow-component-policy.ts`), so only global pages can
 * ever hold one — no goal or portfolio page walk is needed here.
 */
export function MemberSessionFlowView({
  allPages,
  ctx,
  memberName,
  memberRepresentative,
  isExpandedLine = false,
  language,
  isRepresentative = false,
}: MemberSessionFlowViewProps) {
  const t = useT(T);
  const allowedTypes = isRepresentative
    ? REPRESENTATIVE_SESSION_COMPONENT_TYPES
    : MEMBER_SESSION_COMPONENT_TYPES;
  const activeGoalId = ctx.goals[0]?.id ?? null;

  const globalPages = allPages
    .filter((p) => p.pageType === "global")
    .filter((p) =>
      evaluateCondition(p?.conditionalVisibility ?? null, {
        formValues: ctx.formValues,
        goals: ctx.goals,
        fieldTypes: ctx.fieldTypes,
      }),
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // QL-505 — resolve page → visible-component pairs once, both to
  // render them and to know whether ANYTHING is visible at all: a flow with
  // no PA/quiz component on a public page, or one whose conditional rules
  // hide every instance for this client, would otherwise render a bare
  // header with nothing under it.
  const pagesWithComponents = globalPages
    .map((page) => {
      const components = (page.components ?? [])
        .filter((c: any) => allowedTypes.has(c.componentType))
        .filter((component: any) => {
          let conditionalDisplay: ConditionalDisplayConfig | null = null;
          try {
            const config = component.config ? JSON.parse(component.config) : null;
            conditionalDisplay = config?.conditionalDisplay || null;
          } catch {
            // Invalid config
          }
          return evaluateCondition(conditionalDisplay, {
            formValues: ctx.formValues,
            goals: ctx.goals,
            fieldTypes: ctx.fieldTypes,
            goalId: null,
          });
        });
      return { page, components };
    })
    .filter(({ components }) => components.length > 0);

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 bg-card border-b border-border shadow-sm px-4 py-3">
        <p className="text-sm font-medium">
          {memberRepresentative && isExpandedLine
            ? t.answeringAs(onBehalfOfText(memberRepresentative, language))
            : memberRepresentative
              ? t.answeringFor(representativeWithRole(memberRepresentative, language), memberName)
              : t.answeringAs(memberName)}
        </p>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {pagesWithComponents.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-12">{t.nothingToAnswer}</p>
        ) : (
          pagesWithComponents.map(({ page, components }) => (
            <div key={page.id} className="space-y-6">
              {components.map((component: any) => (
                <div key={component.id}>
                  <PublicFlowComponentRenderer
                    component={component}
                    isCompleted={ctx.isCompleted}
                    language={ctx.language}
                    flowInstanceId={ctx.flowInstanceId}
                    riskModelId={ctx.riskModelId}
                    portfolioCollectionId={ctx.portfolioCollectionId}
                    flowCurrency={ctx.flowCurrency}
                    primaryColor={ctx.primaryColor}
                    productTypes={ctx.productTypes}
                    goalTypes={ctx.goalTypes}
                    multiGoal={ctx.multiGoal}
                    maxGoals={ctx.maxGoals}
                    portfolioCosts={ctx.portfolioCosts}
                    currentGoal={null}
                    currentGoalId={activeGoalId}
                    clientType={ctx.clientType}
                    clientName={ctx.clientName}
                    chartColors={ctx.chartColors}
                    onCustomPortfolioSaveComplete={async () => {}}
                    scope="global"
                    showValidationErrors={false}
                    markers={ctx.markers}
                  />
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
